import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useSelectedBusiness } from "../../../redux/store";
import { resolveAlertRecipients } from "../../../util/alerts/residentAssignment";
import {
  isAlertRoutingBackendSyncEnabled,
  pullAlertRoutingSettingsFromBackend,
  pushAlertRoutingSettingsToBackend,
} from "../../../util/alerts/alertRoutingSync";
import TimezoneSelector from "../../../util/timezoneSelector";
import { UserContext } from "../../auth/auth-context";
import {
  getBusinessProductScopes,
  isScopeVisible,
  ProductScope,
} from "../../../../core/business/productScopes";

type Props = {
  businessId?: string | number | null;
  isDisabled?: boolean;
};

const ALERT_TYPE_CONFIGS = [
  { key: "STAFF_DOC_EXPIRED", scope: ProductScope.SHARED },
  { key: "RESIDENT_DOC_EXPIRED", scope: ProductScope.CARE },
  { key: "BUSINESS_DOC_EXPIRED", scope: ProductScope.SHARED },
  { key: "MEDICATION_LATE", scope: ProductScope.CARE },
] as const;
type AlertType = (typeof ALERT_TYPE_CONFIGS)[number]["key"];
const ALERT_TYPES = ALERT_TYPE_CONFIGS.map((item) => item.key) as AlertType[];
const ALERT_LABELS: Record<string, string> = {
  STAFF_DOC_EXPIRED: "Staff Document Expired",
  RESIDENT_DOC_EXPIRED: "Resident Document Expired",
  BUSINESS_DOC_EXPIRED: "Business Document Expired",
  MEDICATION_LATE: "Medication Late",
};
const SMS_ALLOWED_TYPES = new Set<string>([
  "STAFF_DOC_EXPIRED",
  "RESIDENT_DOC_EXPIRED",
  "BUSINESS_DOC_EXPIRED",
  "MEDICATION_LATE",
]);

const CHANNELS = ["IN_APP", "SMS", "EMAIL"] as const;
const SEVERITIES = ["low", "medium", "high", "critical"] as const;
const TIME_12H_OPTIONS = [
  "12:00 AM", "1:00 AM", "2:00 AM", "3:00 AM", "4:00 AM", "5:00 AM",
  "6:00 AM", "7:00 AM", "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
  "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM", "10:00 PM", "11:00 PM",
] as const;

function to12HourLabel(value?: string) {
  const v = String(value || "").trim();
  const m = v.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return "9:00 PM";
  let h = Number(m[1]);
  const mm = m[2];
  const isPm = h >= 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${isPm ? "PM" : "AM"}`;
}

function to24HourValue(label?: string) {
  const t = String(label || "").trim().toUpperCase();
  const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (!m) return "21:00";
  let h = Number(m[1]);
  const mm = m[2];
  const ap = m[3];
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${mm}`;
}

function canUseSmsForType(type?: string) {
  return SMS_ALLOWED_TYPES.has(String(type || "").toUpperCase());
}

function sanitizeRuleSms(type: string, rule: any) {
  if (!rule || canUseSmsForType(type)) return rule;
  const channels = Array.isArray(rule.channels)
    ? rule.channels.filter((c: string) => c !== "SMS")
    : [];
  const escalation = Array.isArray(rule.escalation)
    ? rule.escalation.map((s: any) => ({
      ...s,
      channels: Array.isArray(s?.channels) ? s.channels.filter((c: string) => c !== "SMS") : [],
    }))
    : [];
  return { ...rule, channels, escalation };
}

function sanitizePolicySms(policy: any) {
  const next = {
    ...policy,
    rules: { ...(policy?.rules || {}) },
  };
  Object.keys(next.rules || {}).forEach((type) => {
    next.rules[type] = sanitizeRuleSms(type, next.rules[type]);
  });
  return next;
}

function serializePolicy(policy: any) {
  try {
    return JSON.stringify(policy);
  } catch {
    return "";
  }
}

const DEFAULT_POLICY = {
  version: 1,
  defaults: {
    timezone: "America/Los_Angeles",
    sms: {
      enable: true,
      quietHours: {
        enabled: true,
        start: "21:00",
        end: "07:00",
        allowCritical: true,
      },
    },
    rateLimit: {
      windowMinutes: 60,
      maxPerRecipientPerWindow: 4,
    },
  },
  rules: {
    STAFF_DOC_EXPIRED: {
      severity: "medium",
      channels: ["IN_APP", "SMS"],
      routingMode: "ROLE_BASED",
      primaryRoles: ["Admin", "Advanced"],
      primaryStaffIds: [],
      secondaryRoles: [],
      secondaryStaffIds: [],
      dedupeKey: "STAFF_DOC_EXPIRED:{staffId}:{docType}",
      dedupeTtlMinutes: 1440,
      maxSendsPerDedupeKey: 2,
      escalation: [],
    },
    RESIDENT_DOC_EXPIRED: {
      severity: "medium",
      channels: ["IN_APP", "SMS"],
      routingMode: "ROLE_BASED",
      primaryRoles: ["Admin", "Advanced"],
      primaryStaffIds: [],
      secondaryRoles: ["Basic"],
      secondaryStaffIds: [],
      dedupeKey: "RESIDENT_DOC_EXPIRED:{residentId}:{docType}",
      dedupeTtlMinutes: 1440,
      maxSendsPerDedupeKey: 2,
      escalation: [],
    },
    BUSINESS_DOC_EXPIRED: {
      severity: "high",
      channels: ["IN_APP", "SMS"],
      routingMode: "ROLE_BASED",
      primaryRoles: ["Admin", "Advanced"],
      primaryStaffIds: [],
      secondaryRoles: [],
      secondaryStaffIds: [],
      dedupeKey: "BUSINESS_DOC_EXPIRED:{docType}",
      dedupeTtlMinutes: 1440,
      maxSendsPerDedupeKey: 2,
      escalation: [
        {
          afterMinutes: 120,
          roles: ["Admin"],
          staffIds: [],
          channels: ["SMS"],
        },
      ],
    },
    MEDICATION_LATE: {
      severity: "critical",
      channels: ["IN_APP", "SMS"],
      routingMode: "ASSIGNEE_FIRST",
      primaryRoles: ["Advanced", "Basic"],
      primaryStaffIds: [],
      secondaryRoles: [],
      secondaryStaffIds: [],
      dedupeKey: "MEDICATION_LATE:{marTaskId}",
      dedupeTtlMinutes: 15,
      maxSendsPerDedupeKey: 4,
      escalation: [
        {
          afterMinutes: 10,
          roles: ["Advanced"],
          staffIds: [],
          channels: ["SMS", "IN_APP"],
        },
        {
          afterMinutes: 20,
          roles: ["Admin"],
          staffIds: [],
          channels: ["SMS", "IN_APP"],
        },
      ],
    },
  },
};

export default function BusinessAlertRoutingTab({ businessId, isDisabled = false }: Props) {
  const { providerUser } = React.useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const activeScopes = React.useMemo(
    () => getBusinessProductScopes(providerUser, selectedBusiness),
    [providerUser, selectedBusiness]
  );
  const visibleAlertTypes = React.useMemo(
    () =>
      ALERT_TYPE_CONFIGS.filter((config) =>
        isScopeVisible(config.scope, activeScopes)
      ).map((config) => config.key),
    [activeScopes]
  );
  const staffList = React.useMemo(() => selectedBusiness?.businessStaffs || [], [selectedBusiness?.businessStaffs]);
  const roleList = React.useMemo(() => selectedBusiness?.roles || [], [selectedBusiness?.roles]);

  const staffOptions = React.useMemo(
    () =>
      (staffList || []).map((s: any) => ({
        id: String(s?.id ?? ""),
        label: s?.user?.username || s?.user?.email || `Staff ${s?.id}`,
      })),
    [staffList]
  );

  const roleOptions = React.useMemo(() => {
    const fromRoles = (roleList || []).map((r: any) => String(r?.name || "").trim()).filter(Boolean);
    const fromStaff = (staffList || [])
      .map((s: any) => String(s?.role?.name || "").trim())
      .filter(Boolean);
    return Array.from(new Set([...fromRoles, ...fromStaff]));
  }, [roleList, staffList]);

  const storageKey = React.useMemo(
    () => `business-alert-policy:v1:${String(businessId ?? "default")}`,
    [businessId]
  );

  const [policy, setPolicy] = React.useState<any>(DEFAULT_POLICY);
  const [savedPolicySnapshot, setSavedPolicySnapshot] = React.useState<string>(
    serializePolicy(sanitizePolicySms(DEFAULT_POLICY))
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState("");
  const [errorMsg, setErrorMsg] = React.useState("");
  const [selectedType, setSelectedType] = React.useState<AlertType>(ALERT_TYPES[0]);
  const [previewResidentId, setPreviewResidentId] = React.useState<string>("");
  const [previewAssigneeStaffId, setPreviewAssigneeStaffId] = React.useState<string>("");

  const mergePolicy = React.useCallback((incoming: any) => {
    const merged: any = {
      ...DEFAULT_POLICY,
      ...incoming,
      defaults: {
        ...DEFAULT_POLICY.defaults,
        ...(incoming?.defaults || {}),
        sms: {
          ...DEFAULT_POLICY.defaults.sms,
          ...(incoming?.defaults?.sms || {}),
          quietHours: {
            ...DEFAULT_POLICY.defaults.sms.quietHours,
            ...(incoming?.defaults?.sms?.quietHours || {}),
          },
        },
        rateLimit: {
          ...DEFAULT_POLICY.defaults.rateLimit,
          ...(incoming?.defaults?.rateLimit || {}),
        },
      },
      rules: {
        ...DEFAULT_POLICY.rules,
      },
    };

    ALERT_TYPES.forEach((type) => {
      const src = incoming?.rules?.[type] || {};
      const base = DEFAULT_POLICY.rules[type];
      merged.rules[type] = {
        ...base,
        ...src,
        escalation: Array.isArray(src?.escalation)
          ? src.escalation.map((s: any) => ({
            afterMinutes: Number(s?.afterMinutes ?? 15),
            roles: Array.isArray(s?.roles) ? s.roles : [],
            staffIds: Array.isArray(s?.staffIds) ? s.staffIds.map((id: any) => String(id)) : [],
            channels: Array.isArray(s?.channels) ? s.channels : ["IN_APP"],
          }))
          : base.escalation,
      };
    });

    return merged;
  }, []);

  React.useEffect(() => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      const mergedDefault = mergePolicy(DEFAULT_POLICY);
      setPolicy(mergedDefault);
      setSavedPolicySnapshot(serializePolicy(sanitizePolicySms(mergedDefault)));
      setErrorMsg("");
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      const merged = mergePolicy(parsed);
      setPolicy(merged);
      setSavedPolicySnapshot(serializePolicy(sanitizePolicySms(merged)));
      setErrorMsg("");
    } catch {
      const mergedDefault = mergePolicy(DEFAULT_POLICY);
      setPolicy(mergedDefault);
      setSavedPolicySnapshot(serializePolicy(sanitizePolicySms(mergedDefault)));
      setErrorMsg("Stored policy was invalid and reset to defaults.");
    }
  }, [storageKey, mergePolicy]);

  React.useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!businessId || !isAlertRoutingBackendSyncEnabled()) return;
      const result = await pullAlertRoutingSettingsFromBackend(businessId);
      if (cancelled || !result?.ok || !result?.policy) return;
      const merged = mergePolicy(result.policy);
      setPolicy(merged);
      setSavedPolicySnapshot(serializePolicy(sanitizePolicySms(merged)));
      setStatusMsg("Loaded routing settings from backend.");
      setErrorMsg("");
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [businessId, mergePolicy]);

  const alertTypeOptions = React.useMemo(() => [...visibleAlertTypes], [visibleAlertTypes]);
  const residentOptions = React.useMemo(
    () => (((selectedBusiness as any)?.residents || []) as any[]).filter((r: any) => r?.id != null),
    [selectedBusiness]
  );
  const selectedTypeNeedsResidentContext =
    selectedType === "RESIDENT_DOC_EXPIRED" || selectedType === "MEDICATION_LATE";
  const selectedTypeSupportsExplicitAssignee =
    selectedType === "STAFF_DOC_EXPIRED" || selectedType === "MEDICATION_LATE";

  React.useEffect(() => {
    if (!alertTypeOptions.length) return;
    if (!alertTypeOptions.includes(selectedType)) {
      setSelectedType(alertTypeOptions[0]);
    }
  }, [alertTypeOptions, selectedType]);

  React.useEffect(() => {
    if (!selectedTypeNeedsResidentContext) {
      setPreviewResidentId("");
      return;
    }
    if (!residentOptions.length) {
      setPreviewResidentId("");
      return;
    }
    if (!previewResidentId || !residentOptions.some((r: any) => String(r.id) === String(previewResidentId))) {
      setPreviewResidentId(String(residentOptions[0].id));
    }
  }, [residentOptions, previewResidentId, selectedTypeNeedsResidentContext]);

  React.useEffect(() => {
    if (!selectedTypeSupportsExplicitAssignee) {
      setPreviewAssigneeStaffId("");
      return;
    }
    if (!staffOptions.length) {
      setPreviewAssigneeStaffId("");
      return;
    }
    if (!previewAssigneeStaffId || !staffOptions.some((s) => String(s.id) === String(previewAssigneeStaffId))) {
      setPreviewAssigneeStaffId(String(staffOptions[0].id));
    }
  }, [staffOptions, previewAssigneeStaffId, selectedTypeSupportsExplicitAssignee]);

  const setDefaults = (next: any) => {
    setPolicy((prev: any) => ({ ...prev, defaults: { ...prev.defaults, ...next } }));
    setStatusMsg("");
    setErrorMsg("");
  };

  const setRule = (type: string, next: any) => {
    setPolicy((prev: any) => ({
      ...prev,
      rules: {
        ...prev.rules,
        [type]: { ...prev.rules[type], ...next },
      },
    }));
    setStatusMsg("");
    setErrorMsg("");
  };

  const setEscalation = (type: string, index: number, next: any) => {
    const current = policy?.rules?.[type]?.escalation || [];
    const updated = current.map((s: any, i: number) => (i === index ? { ...s, ...next } : s));
    setRule(type, { escalation: updated });
  };

  const addEscalation = (type: string) => {
    const current = policy?.rules?.[type]?.escalation || [];
    setRule(type, {
      escalation: [
        ...current,
        { afterMinutes: 15, roles: [], staffIds: [], channels: ["IN_APP"] },
      ],
    });
  };

  const removeEscalation = (type: string, index: number) => {
    const current = policy?.rules?.[type]?.escalation || [];
    setRule(type, { escalation: current.filter((_: any, i: number) => i !== index) });
  };

  const toggleChannel = (type: string, channel: string, checked: boolean) => {
    if (channel === "SMS" && !canUseSmsForType(type)) return;
    const current: string[] = policy?.rules?.[type]?.channels || [];
    const next = checked
      ? current.includes(channel)
        ? current
        : [...current, channel]
      : current.filter((c) => c !== channel);
    setRule(type, { channels: next });
  };

  const toggleEscChannel = (type: string, index: number, channel: string, checked: boolean) => {
    if (channel === "SMS" && !canUseSmsForType(type)) return;
    const current: string[] = policy?.rules?.[type]?.escalation?.[index]?.channels || [];
    const next = checked
      ? current.includes(channel)
        ? current
        : [...current, channel]
      : current.filter((c) => c !== channel);
    setEscalation(type, index, { channels: next });
  };

  const getSelectValues = (event: SelectChangeEvent<string[]>) => {
    const v = event.target.value as unknown;
    if (Array.isArray(v)) return v;
    if (typeof v === "string") {
      return v.split(",").map((x) => x.trim()).filter(Boolean);
    }
    return [];
  };

  const currentPolicySnapshot = React.useMemo(
    () => serializePolicy(sanitizePolicySms(policy)),
    [policy]
  );
  const hasUnsavedChanges = currentPolicySnapshot !== savedPolicySnapshot;

  const handleSave = async () => {
    if (isSaving || !hasUnsavedChanges) return;
    setIsSaving(true);
    try {
      const sanitized = sanitizePolicySms(policy);
      const serialized = serializePolicy(sanitized);
      setPolicy(sanitized);
      localStorage.setItem(storageKey, JSON.stringify(sanitized));
      if (isAlertRoutingBackendSyncEnabled() && businessId) {
        const synced = await pushAlertRoutingSettingsToBackend({ businessId, policy: sanitized });
        if (synced?.ok) {
          setStatusMsg("Alert routing settings saved.");
        } else {
          setStatusMsg("Saved locally. Backend sync is pending.");
        }
      } else {
        setStatusMsg("Alert routing settings saved.");
      }
      setSavedPolicySnapshot(serialized);
      setErrorMsg("");
    } catch {
      setStatusMsg("");
      setErrorMsg("Unable to save alert routing settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPolicy(DEFAULT_POLICY);
    setStatusMsg("Reset to default template.");
    setErrorMsg("");
  };


  const getRoutingOrderText = React.useCallback(
    (type: string) => {
      const mode = String(
        policy?.rules?.[type]?.routingMode ||
        (String(type || "").toUpperCase() === "MEDICATION_LATE" ? "ASSIGNEE_FIRST" : "ROLE_BASED")
      ).toUpperCase();
      if (mode === "ASSIGNEE_FIRST") {
        return "Order: explicit assignee, then resident shift assignment, then on-duty role, then policy fallback.";
      }
      if (mode === "ON_DUTY_ROLE") {
        return "Order: explicit assignee, then on-duty role, then resident shift assignment fallback, then policy fallback.";
      }
      return "Order: explicit assignee, then role-based recipients, then resident shift/on-duty fallback, then policy fallback.";
    },
    [policy?.rules]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
        Alert Routing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Configure who gets notified first, which channels to use, and escalation timing.
        Settings save locally and can sync to backend when backend sync is enabled.
      </Typography>

      <Alert severity="info" sx={{ mb: 1.25 }}>
        Shared business alerts always appear here. Care-specific alerts show only when the
        care product is active for this business.
      </Alert>

      {!!statusMsg && <Alert severity="success" sx={{ mb: 1.25 }}>{statusMsg}</Alert>}
      {!!errorMsg && <Alert severity="error" sx={{ mb: 1.25 }}>{errorMsg}</Alert>}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Defaults</Typography>
        <Grid container spacing={1.25}>
          <Grid item xs={12} md={4}>
            <Box sx={{ mt: 0.4 }}>
              <TimezoneSelector
                timezoneId={policy?.defaults?.timezone || ""}
                isDisabled={isDisabled}
                onTimezoneSelect={(tz: any) => {
                  const value = tz?.timezone || tz?.id || "";
                  if (value) setDefaults({ timezone: value });
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Rate limit window (minutes)"
              value={policy?.defaults?.rateLimit?.windowMinutes ?? 60}
              onChange={(e) =>
                setDefaults({
                  rateLimit: {
                    ...policy?.defaults?.rateLimit,
                    windowMinutes: Math.max(1, Number(e.target.value || 1)),
                  },
                })
              }
              disabled={isDisabled}
              inputProps={{ min: 1 }}
              helperText="Applies to all channels (SMS, Email, In-app)."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Max per recipient per window"
              value={policy?.defaults?.rateLimit?.maxPerRecipientPerWindow ?? 4}
              onChange={(e) =>
                setDefaults({
                  rateLimit: {
                    ...policy?.defaults?.rateLimit,
                    maxPerRecipientPerWindow: Math.max(1, Number(e.target.value || 1)),
                  },
                })
              }
              disabled={isDisabled}
              inputProps={{ min: 1 }}
            />
          </Grid>
        </Grid>

        <FormGroup row sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!policy?.defaults?.sms?.enable}
                onChange={(e) =>
                  setDefaults({
                    sms: { ...policy?.defaults?.sms, enable: e.target.checked },
                  })
                }
                disabled={isDisabled}
              />
            }
            label="Enable SMS"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!policy?.defaults?.sms?.quietHours?.enabled}
                onChange={(e) =>
                  setDefaults({
                    sms: {
                      ...policy?.defaults?.sms,
                      quietHours: {
                        ...policy?.defaults?.sms?.quietHours,
                        enabled: e.target.checked,
                      },
                    },
                  })
                }
                disabled={isDisabled}
              />
            }
            label="Quiet hours"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!policy?.defaults?.sms?.quietHours?.allowCritical}
                onChange={(e) =>
                  setDefaults({
                    sms: {
                      ...policy?.defaults?.sms,
                      quietHours: {
                        ...policy?.defaults?.sms?.quietHours,
                        allowCritical: e.target.checked,
                      },
                    },
                  })
                }
                disabled={isDisabled}
              />
            }
            label="Allow critical during quiet hours"
          />
        </FormGroup>

        <Grid container spacing={1.25}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Quiet hours start"
              value={to12HourLabel(policy?.defaults?.sms?.quietHours?.start || "21:00")}
              onChange={(e) =>
                setDefaults({
                  sms: {
                    ...policy?.defaults?.sms,
                    quietHours: {
                      ...policy?.defaults?.sms?.quietHours,
                      start: to24HourValue(e.target.value),
                    },
                  },
                })
              }
              disabled={isDisabled}
            >
              {TIME_12H_OPTIONS.map((t) => (
                <MenuItem key={`qh-start-${t}`} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Quiet hours end"
              value={to12HourLabel(policy?.defaults?.sms?.quietHours?.end || "07:00")}
              onChange={(e) =>
                setDefaults({
                  sms: {
                    ...policy?.defaults?.sms,
                    quietHours: {
                      ...policy?.defaults?.sms?.quietHours,
                      end: to24HourValue(e.target.value),
                    },
                  },
                })
              }
              disabled={isDisabled}
            >
              {TIME_12H_OPTIONS.map((t) => (
                <MenuItem key={`qh-end-${t}`} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Grid container spacing={1.25} sx={{ mb: 1.25 }}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Alert type</InputLabel>
              <Select
                label="Alert type"
                value={selectedType}
                onChange={(e) => setSelectedType(String(e.target.value) as AlertType)}
                disabled={isDisabled}
              >
                {alertTypeOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {ALERT_LABELS[t] || t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={7} />
        </Grid>

        {(() => {
          const type = selectedType;
          const rule = policy?.rules?.[type] || {};
          const esc = rule?.escalation || [];
          return (
            <>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography sx={{ fontWeight: 700 }}>{ALERT_LABELS[type] || type}</Typography>
                <Chip
                  size="small"
                  label={String(rule?.severity || "medium").toUpperCase()}
                  color={rule?.severity === "critical" ? "error" : "default"}
                />
              </Stack>

              <Grid container spacing={1.25}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Severity</InputLabel>
                    <Select
                      label="Severity"
                      value={rule?.severity || "medium"}
                      onChange={(e) => setRule(type, { severity: e.target.value })}
                      disabled={isDisabled}
                    >
                      {SEVERITIES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Routing mode"
                    value={rule?.routingMode || (type === "MEDICATION_LATE" ? "ASSIGNEE_FIRST" : "ROLE_BASED")}
                    onChange={(e) => setRule(type, { routingMode: e.target.value })}
                    select
                    disabled={isDisabled || type === "MEDICATION_LATE"}
                    helperText={
                      type === "MEDICATION_LATE"
                        ? "Medication alerts first notify the assigned staff."
                        : "Role-based routing uses your business roles and selected staff."
                    }
                  >
                    <MenuItem value="ROLE_BASED">Role-based</MenuItem>
                    <MenuItem value="ASSIGNEE_FIRST">Assignee first</MenuItem>
                    <MenuItem value="ON_DUTY_ROLE">On-duty role</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Repeat reminder cooldown (minutes)"
                    value={rule?.dedupeTtlMinutes ?? 60}
                    onChange={(e) =>
                      setRule(type, {
                        dedupeTtlMinutes: Math.max(1, Number(e.target.value || 1)),
                      })
                    }
                    disabled={isDisabled}
                    inputProps={{ min: 1 }}
                    helperText="Prevents sending the same alert too frequently."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max sends for same alert key"
                    value={rule?.maxSendsPerDedupeKey ?? 2}
                    onChange={(e) =>
                      setRule(type, {
                        maxSendsPerDedupeKey: Math.max(1, Number(e.target.value || 1)),
                      })
                    }
                    disabled={isDisabled}
                    inputProps={{ min: 1 }}
                    helperText="After this many sends to the same recipient/channel for this alert key, repeats stop."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Channels
                  </Typography>
                  <FormGroup row>
                    {CHANNELS.map((c) => (
                      (() => {
                        const smsBlocked = c === "SMS" && !canUseSmsForType(type);
                        return (
                      <FormControlLabel
                        key={`${type}-ch-${c}`}
                        control={
                          <Checkbox
                            checked={(rule?.channels || []).includes(c)}
                            onChange={(e) => toggleChannel(type, c, e.target.checked)}
                            disabled={isDisabled || smsBlocked}
                          />
                        }
                        label={c}
                      />
                        );
                      })()
                    ))}
                  </FormGroup>
                  {!canUseSmsForType(type) && (
                    <Typography variant="caption" color="text.secondary">
                      SMS is enabled only for document expiry and medication late alerts.
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Primary roles</InputLabel>
                    <Select
                      multiple
                      label="Primary roles"
                      value={(rule?.primaryRoles || []) as string[]}
                      onChange={(e) => setRule(type, { primaryRoles: getSelectValues(e) })}
                      disabled={isDisabled}
                      renderValue={(selected) => (selected as string[]).join(", ")}
                    >
                      {roleOptions.map((r) => (
                        <MenuItem key={`${type}-pr-${r}`} value={r}>
                          <Checkbox checked={(rule?.primaryRoles || []).includes(r)} />
                          <Typography variant="body2">{r}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Primary staff (optional)</InputLabel>
                    <Select
                      multiple
                      label="Primary staff (optional)"
                      value={(rule?.primaryStaffIds || []).map((id: any) => String(id))}
                      onChange={(e) => setRule(type, { primaryStaffIds: getSelectValues(e) })}
                      disabled={isDisabled}
                      renderValue={(selected) =>
                        (selected as string[])
                          .map((id) => staffOptions.find((s) => s.id === String(id))?.label || id)
                          .join(", ")
                      }
                    >
                      {staffOptions.map((s) => (
                        <MenuItem key={`${type}-ps-${s.id}`} value={s.id}>
                          <Checkbox checked={(rule?.primaryStaffIds || []).map(String).includes(String(s.id))} />
                          <Typography variant="body2">{s.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Secondary roles</InputLabel>
                    <Select
                      multiple
                      label="Secondary roles"
                      value={(rule?.secondaryRoles || []) as string[]}
                      onChange={(e) => setRule(type, { secondaryRoles: getSelectValues(e) })}
                      disabled={isDisabled}
                      renderValue={(selected) => (selected as string[]).join(", ")}
                    >
                      {roleOptions.map((r) => (
                        <MenuItem key={`${type}-sr-${r}`} value={r}>
                          <Checkbox checked={(rule?.secondaryRoles || []).includes(r)} />
                          <Typography variant="body2">{r}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Secondary staff (optional)</InputLabel>
                    <Select
                      multiple
                      label="Secondary staff (optional)"
                      value={(rule?.secondaryStaffIds || []).map((id: any) => String(id))}
                      onChange={(e) => setRule(type, { secondaryStaffIds: getSelectValues(e) })}
                      disabled={isDisabled}
                      renderValue={(selected) =>
                        (selected as string[])
                          .map((id) => staffOptions.find((s) => s.id === String(id))?.label || id)
                          .join(", ")
                      }
                    >
                      {staffOptions.map((s) => (
                        <MenuItem key={`${type}-ss-${s.id}`} value={s.id}>
                          <Checkbox checked={(rule?.secondaryStaffIds || []).map(String).includes(String(s.id))} />
                          <Typography variant="body2">{s.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.2 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Escalations</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addEscalation(type)}
                  disabled={isDisabled}
                >
                  Add Step
                </Button>
              </Stack>

              <Stack spacing={1} sx={{ mt: 1 }}>
                {esc.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No escalation steps.
                  </Typography>
                )}
                {esc.map((step: any, idx: number) => (
                  <Paper key={`${type}-esc-${idx}`} variant="outlined" sx={{ p: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Step {idx + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeEscalation(type, idx)}
                        disabled={isDisabled}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="After minutes"
                          value={step?.afterMinutes ?? 15}
                          onChange={(e) =>
                            setEscalation(type, idx, {
                              afterMinutes: Math.max(1, Number(e.target.value || 1)),
                            })
                          }
                          disabled={isDisabled}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Notify roles</InputLabel>
                          <Select
                            multiple
                            label="Notify roles"
                            value={(step?.roles || []) as string[]}
                            onChange={(e) => setEscalation(type, idx, { roles: getSelectValues(e) })}
                            disabled={isDisabled}
                            renderValue={(selected) => (selected as string[]).join(", ")}
                          >
                            {roleOptions.map((r) => (
                              <MenuItem key={`${type}-esc-${idx}-role-${r}`} value={r}>
                                <Checkbox checked={(step?.roles || []).includes(r)} />
                                <Typography variant="body2">{r}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Notify staff (optional)</InputLabel>
                          <Select
                            multiple
                            label="Notify staff (optional)"
                            value={(step?.staffIds || []).map((id: any) => String(id))}
                            onChange={(e) => setEscalation(type, idx, { staffIds: getSelectValues(e) })}
                            disabled={isDisabled}
                            renderValue={(selected) =>
                              (selected as string[])
                                .map((id) => staffOptions.find((s) => s.id === String(id))?.label || id)
                                .join(", ")
                            }
                          >
                            {staffOptions.map((s) => (
                              <MenuItem key={`${type}-esc-${idx}-staff-${s.id}`} value={s.id}>
                                <Checkbox checked={(step?.staffIds || []).map(String).includes(String(s.id))} />
                                <Typography variant="body2">{s.label}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Escalation channels
                        </Typography>
                        <FormGroup row>
                          {CHANNELS.map((c) => (
                            (() => {
                              const smsBlocked = c === "SMS" && !canUseSmsForType(type);
                              return (
                            <FormControlLabel
                              key={`${type}-esc-${idx}-${c}`}
                              control={
                                <Checkbox
                                  checked={(step?.channels || []).includes(c)}
                                  onChange={(e) => toggleEscChannel(type, idx, c, e.target.checked)}
                                  disabled={isDisabled || smsBlocked}
                                />
                              }
                              label={c}
                            />
                              );
                            })()
                          ))}
                        </FormGroup>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </>
          );
        })()}
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
        <Typography sx={{ fontWeight: 700, mb: 1 }}>Routing Preview</Typography>
        <Alert
          severity="info"
          sx={{
            mb: 1,
            border: "1px solid #90caf9",
            bgcolor: "#e3f2fd",
            "& .MuiAlert-message": { fontWeight: 700, color: "#0d47a1" },
          }}
        >
          {getRoutingOrderText(selectedType)}
        </Alert>
        <Grid container spacing={1.25}>
          {(() => {
            const needsResidentContext = selectedTypeNeedsResidentContext;
            const supportsExplicitAssignee = selectedTypeSupportsExplicitAssignee;

            const resident = residentOptions.find((r: any) => String(r.id) === String(previewResidentId));
            const explicitAssigneeId = supportsExplicitAssignee
              ? Number(previewAssigneeStaffId || 0) || null
              : null;

            const showMissingResidentWarning = needsResidentContext && !resident;
            const previewInput: any = {
              business: selectedBusiness,
              resident: resident || null,
              businessId,
              alertType: selectedType,
              explicitAssigneeId,
            };
            const resolved = showMissingResidentWarning
              ? { recipients: [], reasons: [] }
              : resolveAlertRecipients(previewInput);

            const recipients = resolved?.recipients || [];
            const reasons = Array.isArray(resolved?.reasons) ? resolved.reasons : [];

            return (
              <>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    {needsResidentContext && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Resident Context</InputLabel>
                        <Select
                          label="Resident Context"
                          value={previewResidentId}
                          onChange={(e) => setPreviewResidentId(String(e.target.value))}
                        >
                          {residentOptions.map((r: any) => (
                            <MenuItem key={`pv-r-${r.id}`} value={String(r.id)}>
                              {`${r?.firstName || ""} ${r?.lastName || ""}`.trim() || `Resident ${r.id}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {supportsExplicitAssignee && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Explicit Assignee (optional)</InputLabel>
                        <Select
                          label="Explicit Assignee (optional)"
                          value={previewAssigneeStaffId}
                          onChange={(e) => setPreviewAssigneeStaffId(String(e.target.value))}
                        >
                          {staffOptions.map((s) => (
                            <MenuItem key={`pv-a-${s.id}`} value={String(s.id)}>
                              {s.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {!needsResidentContext && (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        This alert type is not resident-specific; preview uses business-level routing.
                      </Alert>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  {showMissingResidentWarning ? (
                    <Alert severity="info">Select a resident to preview this alert type.</Alert>
                  ) : (
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Recipients
                      </Typography>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {recipients.length === 0 && (
                          <Chip size="small" label="No recipients resolved" />
                        )}
                        {recipients.map((s: any) => (
                          <Chip
                            key={`pv-st-${s?.id}`}
                            size="small"
                            variant="outlined"
                            label={s?.user?.username || s?.user?.email || `Staff ${s?.id}`}
                          />
                        ))}
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        Why these recipients
                      </Typography>
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {reasons.length === 0 && <Chip size="small" label="No routing reason available" />}
                        {reasons.map((reason: string, idx: number) => (
                          <Chip key={`pv-rsn-${idx}`} size="small" color="default" label={reason} />
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Grid>
              </>
            );
          })()}
        </Grid>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isDisabled || isSaving || !hasUnsavedChanges}
        >
          Save Alert Routing
        </Button>
        <Button variant="text" onClick={handleReset}>
          Reset Defaults
        </Button>
      </Stack>
    </Box>
  );
}
